<script setup>
import { moment } from "@/utils/useTimeZone";
import { computed, ref } from "vue";

const props = defineProps({
  modelValue: Boolean,
  booking: {
    type: Object,
    required: true,
  },
  activities: {
    type: Object,
    required: true,
  },
  referrers: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue", "update:booking"]);

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const selectedReferrer = ref("");

// Format booking time
const formatBookingTime = computed(() => {
  if (!props.booking?.startTime) return "";

  const bookingDate = moment(props.booking.startTime);
  const timeStr = bookingDate.format("h:mm A");

  if (bookingDate.isSame(moment(), "day")) {
    return `${timeStr} Today`;
  } else if (bookingDate.isSame(moment().add(1, "day"), "day")) {
    return `${timeStr} Tomorrow`;
  } else {
    return `${timeStr} on ${bookingDate.format("dddd, MMMM Do")}`;
  }
});

// Update booking referrer
const updateBookingReferrer = (input) => {
  if (typeof input === "object" && input !== null) {
    // Handle selection from dropdown
    selectedReferrer.value = input.value;
  } else {
    // Handle custom text input
    selectedReferrer.value = input;
  }
};

const closeDialog = () => {
  dialog.value = false;
  emit("closeDialog", selectedReferrer.value);
};

const referrerOptions = computed(() =>
  // Transform referrers array into options format
  props.referrers.map((referrer) => ({
    title: `${referrer.name}${referrer.notes !== "" ? ` - ${referrer.notes}` : ""}`,
    value: `${referrer.name}${referrer.notes !== "" ? ` - ${referrer.notes}` : ""}`,
    disabled: !referrer.enabled,
  })),
);
</script>

<template>
  <VDialog v-model="dialog" persistent max-width="600px">
    <VCard>
      <VCardTitle class="text-h5 pa-4">
        {{ activities?.name }} Booking Confirmed
      </VCardTitle>

      <VCardText>
        <VContainer>
          <!-- Confirmation Message -->
          <VRow>
            <VCol cols="12">
              <div class="text-body-1">Confirmation code: {{ booking.id }}</div>
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12">
              <div class="text-body-1">
                Your {{ activities?.name }} booking is confirmed for
                {{ formatBookingTime }}
              </div>
              <div class="text-body-2 mt-2">
                If you have provided an email address we will email you the
                details.
              </div>
            </VCol>
          </VRow>

          <!-- Booking Agreement -->
          <VRow>
            <VCol cols="12">
              <div class="text-caption text-grey-darken-1">
                {{ activities?.bookingAgreement }}
              </div>
            </VCol>
          </VRow>

          <!-- Referral Section -->
          <VRow class="mt-4 mb-4">
            <VCol cols="12">
              <div class="text-h6">How did you hear about us?</div>
              <div class="text-subtitle-2 mb-4">
                Please take a second to indicate how you heard about our venue
              </div>
              <VCombobox
                :items="referrerOptions"
                :item-props="{ title: 'title', value: 'value' }"
                label="Select or type your answer"
                :return-object="false"
                clearable
                @update:model-value="updateBookingReferrer"
              >
                <template v-slot:item="{ props, item }">
                  <VListItem v-bind="props" :title="item.raw.title" />
                </template>
              </VCombobox>
            </VCol>
          </VRow>
        </VContainer>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn color="primary" variant="flat" @click="closeDialog"> Close </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
<style scoped lang="scss">
.v-dialog > .v-overlay__content > .v-card > .v-card-text {
  padding: 0px 20px;
}
.v-container {
  padding: 0px 16px;
}
</style>
